import { REVEAL_WINDOW_MS, SPEED_THRESHOLD_MS, SPREAD_MIN_MS } from './constants';
import type { FactId } from './facts';
import type { ExerciseType } from './session';

export type MasteryState = 'never' | 'erring' | 'slow' | 'mastered' | 'insufficient';

const ERROR_WINDOW = 5;
const SPEED_WINDOW = 3;

export interface AttemptView {
  factIds: readonly FactId[];
  exerciseType: ExerciseType;
  isCorrect: boolean;
  answerMs: number;
  resumedAfterBackground: boolean;
  /** Время попытки на монотонной шкале. */
  atMs: number;
  /** Порядок в журнале (гибридные часы, сведённые к числу). */
  order: number;
}

export interface RevealView {
  factId: FactId;
  atMs: number;
}

export interface MasteryPolicy {
  thresholdMs: number;
  revealWindowMs: number;
  spreadMinMs: number;
}

const DEFAULT_POLICY: MasteryPolicy = {
  thresholdMs: SPEED_THRESHOLD_MS,
  revealWindowMs: REVEAL_WINDOW_MS,
  spreadMinMs: SPREAD_MIN_MS,
};

/** Правило освоенности живёт здесь в единственном экземпляре. У него два
 *  входа — поштучный `masteryFor` и групповой `masteryForAll`, — и оба
 *  обязаны давать один ответ; развести их означало бы завести второе,
 *  тихо расходящееся определение освоенности.
 *
 *  `touching` — попытки, задевающие факт, уже упорядоченные по `order`.
 *  `factReveals` — раскрытия этого факта. */
function classify(
  touching: readonly AttemptView[],
  factReveals: readonly RevealView[],
  policy: MasteryPolicy,
): MasteryState {
  const { thresholdMs, revealWindowMs, spreadMinMs } = policy;
  if (touching.length === 0) return 'never';

  /** Зачётны только задания с одним фактом: таймер сравнения меряет два
   *  выражения сразу и разделить его нельзя (раздел 6.2). */
  const qualifying = touching.filter((a) => a.exerciseType !== 'compare');

  // Окно ошибок — последние 5 зачётных попыток, любые.
  const errorWindow = qualifying.slice(-ERROR_WINDOW);
  if (errorWindow.some((a) => !a.isCorrect)) return 'erring';

  /** Годность замера вычисляется здесь, при чтении, а не проставляется при
   *  записи: смена политики должна пересчитывать историю (раздел 6.2). */
  const timed = qualifying.filter((a) => {
    /** Нечисло и неположительное время — не замер, а мусор.
     *
     *  Исключаем, а не роняем, и это осознанная разница с логическими
     *  часами. Там испорченная отметка отравляет всё будущее, здесь —
     *  портит один замер. Уронить чтение освоенности значило бы закрыть
     *  ребёнку экран успехов из-за одной кривой строки, которую он
     *  никогда не сможет починить.
     *
     *  Молчаливым это исключение не является: попытка остаётся в журнале и
     *  считается в сводке занятия, из беглости выпадает только её ЗАМЕР —
     *  ровно так же, как у попытки после возврата из другого приложения.
     *
     *  Почему `<= 0`: сравнение `answerMs > threshold` для нуля и
     *  отрицательного ложно, то есть такая попытка считалась бы БЕГЛОЙ. */
    if (!Number.isFinite(a.answerMs) || a.answerMs <= 0) return false;
    if (a.resumedAfterBackground) return false;
    return !factReveals.some((r) => a.atMs >= r.atMs && a.atMs - r.atMs < revealWindowMs);
  });

  // Окно скорости независимо от окна ошибок: верные неизмеренные попытки
  // не должны стирать накопленные замеры.
  // «Отвечает ли быстро сейчас» — последние три годных замера, все быстрые.
  const speedWindow = timed.slice(-SPEED_WINDOW);
  if (speedWindow.length < SPEED_WINDOW) return 'insufficient';
  if (speedWindow.some((a) => a.answerMs > thresholdMs)) return 'slow';

  /** «Удерживает ли знание» — размах ВСЕЙ чистой серии (годных замеров
   *  после последней ошибки; если ошибок не было — все годные замеры), а
   *  не только последних трёх. Иначе интенсивная практика в один вечер
   *  (три замера подряд в одном сеансе) навсегда запирает разброс внутри
   *  нескольких минут и статус недостижим.
   *
   *  Ошибка ищется среди `timed`, а не среди всех зачётных попыток:
   *  неверная попытка почти всегда сама негодна как замер (её раскрытие
   *  ложится в её же 12-часовое окно годности), и тогда она отсекается
   *  фильтром `timed` выше. Если считать серию оборванной по ошибке,
   *  которая физически не присутствует среди годных замеров, серия
   *  обнуляется без единого нового замера — старая беглость стирается
   *  вхолостую, хотя после ошибки ничего измеримого ещё не случилось. */
  let streakStart = timed.length;
  while (streakStart > 0 && timed[streakStart - 1].isCorrect) streakStart--;
  const streak = timed.slice(streakStart);
  if (streak.length === 0) return 'insufficient';

  const spread = streak[streak.length - 1].atMs - streak[0].atMs;
  if (spread >= spreadMinMs) return 'mastered';

  return 'insufficient';
}

export function masteryFor(
  factId: FactId,
  attempts: readonly AttemptView[],
  reveals: readonly RevealView[],
  policy: Partial<MasteryPolicy> = {},
): MasteryState {
  const touching = attempts
    .filter((a) => a.factIds.includes(factId))
    .sort((a, b) => a.order - b.order);
  const factReveals = reveals.filter((r) => r.factId === factId);
  return classify(touching, factReveals, { ...DEFAULT_POLICY, ...policy });
}

/** Состояния сразу многих фактов за один проход по журналу.
 *
 *  Существует ради экрана прогресса: там 180 фактов, и поштучная форма
 *  прочитала бы весь журнал 180 раз. Здесь журнал раскладывается по
 *  фактам однажды, а дальше каждый факт смотрит только свои попытки.
 *
 *  Факты без единой попытки в ответе присутствуют — со значением `never`.
 *  Ключи ответа ровно те, что переданы: экран прогресса рассчитывает
 *  получить все 180 и посчитать по ним доли колец. */
export function masteryForAll(
  factIds: readonly FactId[],
  attempts: readonly AttemptView[],
  reveals: readonly RevealView[],
  policy: Partial<MasteryPolicy> = {},
): Map<FactId, MasteryState> {
  const merged = { ...DEFAULT_POLICY, ...policy };
  const wanted = new Set(factIds);

  const byFact = new Map<FactId, AttemptView[]>();
  const revealsByFact = new Map<FactId, RevealView[]>();
  for (const factId of factIds) {
    byFact.set(factId, []);
    revealsByFact.set(factId, []);
  }

  /** Сортируется журнал целиком, один раз, а не выборка каждого факта:
   *  корзины наполняются в порядке `order` и остаются упорядоченными. */
  const ordered = [...attempts].sort((a, b) => a.order - b.order);
  for (const attempt of ordered) {
    for (const factId of attempt.factIds) {
      // Сравнение задевает два факта, и попытка ложится в обе корзины.
      if (wanted.has(factId)) byFact.get(factId)!.push(attempt);
    }
  }
  for (const reveal of reveals) {
    if (wanted.has(reveal.factId)) revealsByFact.get(reveal.factId)!.push(reveal);
  }

  const result = new Map<FactId, MasteryState>();
  for (const factId of factIds) {
    result.set(factId, classify(byFact.get(factId)!, revealsByFact.get(factId)!, merged));
  }
  return result;
}

export interface MasteryDelta {
  /** Факты, ставшие освоенными за эту тренировку. */
  gained: FactId[];
  /** Факты, вернувшиеся в работу. */
  lost: FactId[];
}

/** Прирост освоенности за сессию (раздел 6.1): итоги показывают не рекорд,
 *  а сколько примеров стало освоенными и сколько вернулось в работу.
 *  `before` — журнал до сессии, `after` — он же вместе с её попытками. */
export function masteryDelta(
  factIds: readonly FactId[],
  before: readonly AttemptView[],
  after: readonly AttemptView[],
  reveals: readonly RevealView[],
  policy: Partial<MasteryPolicy> = {},
): MasteryDelta {
  const wasAll = masteryForAll(factIds, before, reveals, policy);
  const nowAll = masteryForAll(factIds, after, reveals, policy);
  const gained: FactId[] = [];
  const lost: FactId[] = [];
  for (const factId of factIds) {
    const was = wasAll.get(factId) === 'mastered';
    const now = nowAll.get(factId) === 'mastered';
    if (!was && now) gained.push(factId);
    if (was && !now) lost.push(factId);
  }
  return { gained, lost };
}
